import { useEffect, useRef } from 'react';

import { toaster } from '#components/ui/toaster';
import { useBuildInfo } from '#providers/build-info-provider';
import { getRegistration } from '#utils/sw-registration';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

function showUpdateToast(registration: ServiceWorkerRegistration | null) {
  toaster.create({
    title: 'Update available',
    description: 'A new version of the app is available.',
    type: 'info',
    duration: Number.POSITIVE_INFINITY,
    action: {
      label: 'Refresh',
      onClick: () => {
        registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      },
    },
  });
}

/**
 * Polls /build-info.json and listens for SW "waiting"; shows a toast with
 * "Refresh" when a new version is available. Only runs in production.
 * Polling runs only while the tab is visible (Page Visibility API).
 */
export function UpdateHandler() {
  const currentBuildInfo = useBuildInfo();
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const updateToastShownRef = useRef(false);
  const pollIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const showIfNew = () => {
      if (updateToastShownRef.current) return;

      updateToastShownRef.current = true;
      showUpdateToast(registrationRef.current);
    };

    // Use shared registration; subscribe to waiting worker
    void getRegistration().then((registration) => {
      if (!registration) return;

      registrationRef.current = registration;

      const onUpdateFound = () => {
        const worker = registration.installing ?? registration.waiting;
        if (!worker) return;

        const onStateChange = () => {
          if (
            worker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            showIfNew();
          }
        };
        worker.addEventListener('statechange', onStateChange);
        if (
          worker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          showIfNew();
        }
      };

      if (registration.waiting && navigator.serviceWorker.controller) {
        showIfNew();
      }

      registration.addEventListener('updatefound', onUpdateFound);
    });

    // Poll only while tab is visible
    const currentCommit = currentBuildInfo.commit;

    const runCheck = async () => {
      if (updateToastShownRef.current) return;

      try {
        const res = await fetch('/build-info.json', { cache: 'no-store' });
        if (!res.ok) return;

        const data = (await res.json()) as { commit?: string };
        if (data.commit != null && data.commit !== currentCommit) {
          showIfNew();
          if (pollIdRef.current != null) {
            clearInterval(pollIdRef.current);
            pollIdRef.current = null;
          }
        }
      } catch {
        // Ignore network errors; will retry when visible again
      }
    };

    const startPolling = () => {
      if (pollIdRef.current != null || updateToastShownRef.current) return;

      runCheck(); // one immediate check when we become visible
      pollIdRef.current = setInterval(runCheck, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollIdRef.current != null) {
        clearInterval(pollIdRef.current);
        pollIdRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    if (document.visibilityState === 'visible') {
      startPolling();
    }

    visibilityCleanupRef.current = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPolling();
    };

    return () => {
      visibilityCleanupRef.current?.();
      visibilityCleanupRef.current = null;
    };
  }, []);

  return null;
}
